import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { api, extUrl } from "../lib/api";
import { LanguageProvider, useLang, setSiteOverrides } from "../lib/i18n";
import { LogoMark } from "../components/Logo";
import Seo from "../components/Seo";
import Navbar from "../components/site/Navbar";
import Footer from "../components/site/Footer";

// Official Exely integration snippet provided by the property (integration wizard):
// loader + setContext + inline "booking-form" embed. Injected verbatim once the
// container exists - the embed inherits deep-link params (date, nights, adults,
// room-type, promoCodePlain) from this page's URL and jumps straight to results.
const EXELY_SNIPPET = `!function(e,n){
    var t="bookingengine",o="integration",i=e[t]=e[t]||{},a=i[o]=i[o]||{},r="__cq",c="__loader",d="getElementsByTagName";
    if(n=n||[],a[r]=a[r]?a[r].concat(n):n,!a[c]){a[c]=!0;var l=e.document,g=l[d]("head")[0]||l[d]("body")[0];
    !function n(i){if(0!==i.length){var a=l.createElement("script");a.type="text/javascript",a.async=!0,a.src="https://"+i[0]+"/integration/loader.js",
    a.onerror=a.onload=function(n,i){return function(){e[t]&&e[t][o]&&e[t][o].loaded||(g.removeChild(n),i())}}(a,(function(){n(i.slice(1,i.length))})),g.appendChild(a)}}(
    ["id-ibe.hopenapi.com", "ibe.hopenapi.com", "ibe.behopenapi.com"])}
}(window, [
        ["setContext", "BE-INT-nusadiralembongan-com_2026-10-19", "en"],
        ["embed", "booking-form", { container: "be-booking-form" }]
    ]);`;

function BookingPageInner() {
  const { t } = useLang();
  const { search } = useLocation();
  const [site, setSite] = useState(null);
  const [engineReady, setEngineReady] = useState(false);
  const injected = useRef(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api
      .get("/site")
      .then((r) => {
        setSiteOverrides(r.data.content || {});
        setSite(r.data);
      })
      .catch(() => setSite({ error: true }));
  }, []);

  // Inject the vendor snippet once (StrictMode-safe) and watch for the VISIBLE engine iframe.
  // The loader creates a hidden placeholder frame first, then the real one - so only count
  // a frame that has a src and is not display:none.
  useEffect(() => {
    if (!site) return undefined;
    if (!injected.current) {
      injected.current = true;
      const script = document.createElement("script");
      script.type = "text/javascript";
      script.text = EXELY_SNIPPET;
      document.head.appendChild(script);
    }
    const startedAt = Date.now();
    const watch = window.setInterval(() => {
      const el = document.getElementById("be-booking-form");
      const frame =
        el && [...el.querySelectorAll("iframe")].find((f) => f.src && f.style.display !== "none" && Number(f.style.height.replace("px", "")) > 100);
      if (frame) {
        setEngineReady(true);
        window.clearInterval(watch);
      } else if (Date.now() - startedAt > 45000) {
        setEngineReady(true); // stop the overlay; the engine may still appear beneath
        window.clearInterval(watch);
      }
    }, 500);
    return () => window.clearInterval(watch);
  }, [site]);

  const settings = site?.settings || {};

  // External standalone URL carrying the same deep-link params (manual fallback only)
  const externalUrl = useMemo(() => {
    const base = extUrl(settings.booking_engine_url || "");
    if (!base.includes("hopenapi.com")) return base;
    const [beforeHash] = base.split("#");
    const [path, query] = beforeHash.split("?");
    const params = new URLSearchParams(query || "");
    new URLSearchParams(search).forEach((value, key) => {
      if (value) params.set(key, value);
    });
    return `${path}?${params.toString()}#/search`;
  }, [settings.booking_engine_url, search]);

  if (!site) {
    return (
      <div data-testid="booking-page-loader" className="min-h-screen flex items-center justify-center bg-[#1E1A17]">
        <LogoMark className="w-12 h-12 animate-pulse" color="#C9A66B" />
      </div>
    );
  }

  return (
    <div data-testid="booking-page" className="bg-[#FBF9F5] min-h-screen flex flex-col">
      <Seo
        title="Book Your Stay | Nusadira Lembongan Villa"
        description="Reserve Nusadira Lembongan Villa directly. Choose your dates, villa and rate with our best direct rate, secure payment and instant confirmation."
      />
      <Navbar
        bookingUrl={settings.booking_engine_url}
        logoUrl={settings.logo_url}
        logoLightUrl={settings.logo_light_url}
        navMenu={settings.nav_menu}
        hiddenSections={String(settings.hidden_sections || "").split(",").map((s) => s.trim()).filter(Boolean)}
      />

      <main className="flex-1">
        <section className="bg-[#1E1A17] pt-[130px] pb-14 text-center px-5">
          <p className="text-xs uppercase tracking-[0.3em] font-sans font-semibold text-[#C9A66B] mb-4">{t("book.pageEyebrow")}</p>
          <h1 data-testid="booking-page-title" className="font-serif font-normal tracking-tight text-[#F4EFEA] leading-tight text-4xl sm:text-5xl">
            {t("book.pageTitle")}
          </h1>
          <p className="mt-4 text-sm text-[#F4EFEA]/70 font-sans font-light max-w-xl mx-auto leading-relaxed">
            {t("book.pageSubtitle")}
          </p>
        </section>

        <section className="max-w-7xl mx-auto w-full px-3 sm:px-8 py-10 sm:py-14">
          <div className="relative bg-white border border-[#E5DED4] rounded-md overflow-hidden shadow-[0_18px_50px_rgba(30,26,23,0.08)]">
            {!engineReady && (
              <div data-testid="booking-frame-loading" className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-4 bg-white">
                <LogoMark className="w-10 h-10 animate-pulse" color="#9A7B66" />
                <p className="text-[11px] uppercase tracking-[0.22em] text-[#A0988E] font-sans">{t("book.loading")}</p>
              </div>
            )}
            <div id="be-booking-form" data-testid="booking-inline-engine" className="w-full min-h-[640px]" />
          </div>
          {externalUrl && (
            <div className="mt-6 text-center">
              <button
                type="button"
                data-testid="booking-open-fullpage"
                onClick={() => {
                  window.location.href = externalUrl;
                }}
                className="inline-flex items-center gap-2 text-[11px] font-sans font-semibold uppercase tracking-[0.16em] text-[#9A7B66] hover:text-[#826552] transition-colors cursor-pointer"
              >
                {t("book.openFull")}
                <ExternalLink className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </section>
      </main>

      <Footer settings={settings} />
    </div>
  );
}

export default function BookingPage() {
  return (
    <LanguageProvider>
      <BookingPageInner />
    </LanguageProvider>
  );
}
